import { forwardRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Loader2, Lock, RefreshCw } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import type { SectionPodcastSummary } from '@/lib/api-client';
import { useOptionalPodcastPlayer } from '@/contexts/PodcastPlayerContext';
import {
  useGenerateSectionPodcast,
  useListenToSection,
  useSectionPodcastSummary,
} from '@/hooks/lesson/useSectionPodcasts';
import { PlayPauseButton, SeekBar, SpeedChip } from './PodcastControls';
import { podcastHref } from './PodcastPillMenu';
import { podcastPhase } from './podcastPhase';

const FRAME =
  'not-prose my-6 flex items-center gap-3 rounded-xl border border-foreground/10 bg-card/60 p-3 sm:gap-4 sm:p-4';

interface SectionAudioStripProps {
  lessonId: string;
  sectionIndex: number;
  className?: string;
}

/** Icon, a title line and a quieter line under it, then an action. */
function StripRow({
  icon,
  title,
  sub,
  action,
}: {
  icon: ReactNode;
  title: string;
  sub: string;
  action?: ReactNode;
}) {
  return (
    <>
      {icon}
      <div className='min-w-0 flex-1'>
        <p className='text-sm font-semibold text-foreground'>{title}</p>
        <p className='mt-0.5 text-xs text-muted-foreground'>{sub}</p>
      </div>
      {action}
    </>
  );
}

const quietIcon = (Icon: typeof Headphones, spin = false) => (
  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-foreground/10 text-muted-foreground'>
    <Icon
      className={cn(
        'h-4 w-4',
        spin && 'animate-spin motion-reduce:animate-none'
      )}
      aria-hidden='true'
    />
  </span>
);

/**
 * "Listen to this section", in the text under the section's title, the way
 * an article's audio sits under its headline. It makes the section's podcast
 * on demand, then becomes the player for it in place - play, position,
 * speed, the transcript. The audio is the app's one player, so it keeps
 * playing as the student reads on (`PodcastDock`) or leaves the tab (the
 * Navbar pill).
 */
export const SectionAudioStrip = forwardRef<
  HTMLDivElement,
  SectionAudioStripProps
>(({ lessonId, sectionIndex, className }, ref) => {
  const summary = useSectionPodcastSummary(lessonId, sectionIndex);
  if (!summary) return null;
  return (
    <div
      ref={ref}
      role='group'
      aria-label={`Podcast of section ${sectionIndex}`}
      className={cn(FRAME, className)}
    >
      <StripBody lessonId={lessonId} summary={summary} />
    </div>
  );
});
SectionAudioStrip.displayName = 'SectionAudioStrip';

function StripBody({
  lessonId,
  summary,
}: {
  lessonId: string;
  summary: SectionPodcastSummary;
}) {
  const generate = useGenerateSectionPodcast(lessonId);
  const { listen, loading, player } = useListenToSection(lessonId);
  const index = summary.index;
  const make = () => generate.mutate(index);

  switch (podcastPhase(summary, generate.isPending)) {
    case 'ready':
      return player?.isCurrent(lessonId, index) ? (
        <LoadedPlayer lessonId={lessonId} index={index} />
      ) : (
        <ReadyRow
          summary={summary}
          busy={loading === index}
          onPlay={() => void listen(index)}
        />
      );
    case 'making':
      return (
        <StripRow
          icon={quietIcon(Loader2, true)}
          title="Recording this section's podcast"
          sub='Two hosts are talking it through - about a minute.'
        />
      );
    case 'failed':
      return (
        <StripRow
          icon={quietIcon(RefreshCw)}
          title="This section's podcast didn't finish"
          sub='Something went wrong while recording it.'
          action={
            <Button size='sm' variant='outline' onClick={make}>
              Try again
            </Button>
          }
        />
      );
    case 'locked':
      return (
        <StripRow
          icon={quietIcon(Lock)}
          title='Listen to this section'
          sub="On Free, a lesson's first section gets a podcast. Plus has one for every section."
          action={
            <Button size='sm' variant='outline' asChild>
              <Link to='/settings?tab=payment'>See Plus</Link>
            </Button>
          }
        />
      );
    default:
      return (
        <StripRow
          icon={quietIcon(Headphones)}
          title='Listen to this section'
          sub='Two hosts talk it through. It takes about a minute to make.'
          action={
            <Button size='sm' onClick={make}>
              Make podcast
            </Button>
          }
        />
      );
  }
}

/** Made, not yet playing: the big play button and its length. */
function ReadyRow({
  summary,
  busy,
  onPlay,
}: {
  summary: SectionPodcastSummary;
  busy: boolean;
  onPlay: () => void;
}) {
  return (
    <StripRow
      icon={
        <PlayPauseButton
          playing={false}
          busy={busy}
          onClick={onPlay}
          label={`podcast of section ${summary.index}`}
        />
      }
      title='Listen to this section'
      sub={`${Math.max(1, Math.round(summary.duration / 60))} min · Two hosts`}
    />
  );
}

/** This section, loaded in the player: the controls themselves. */
function LoadedPlayer({
  lessonId,
  index,
}: {
  lessonId: string;
  index: number;
}) {
  const player = useOptionalPodcastPlayer();
  if (!player) return null;
  return (
    <>
      <PlayPauseButton
        playing={player.isPlaying}
        onClick={player.toggle}
        label={`podcast of section ${index}`}
      />
      <div className='min-w-0 flex-1'>
        <SeekBar
          compact
          peaks={player.track?.peaks ?? []}
          currentTime={player.currentTime}
          duration={player.duration}
          onSeek={player.seek}
        />
        <Link
          to={podcastHref(lessonId, index)}
          className='tap-target inline-block text-xs font-medium text-primary underline-offset-4 hover:underline'
        >
          Transcript
        </Link>
      </div>
      <SpeedChip rate={player.rate} onChange={player.setRate} />
    </>
  );
}
